// ═══════════════════════════════════════════════════════════════════════════
// PAGE: GLOBAL ACTIONS LIST — render orchestration (lot B.9/2)
// Migration S5.2 du plan TS+Vite (2026-05-10) : .js → .ts strict.
// ═══════════════════════════════════════════════════════════════════════════
//
// renderActionsPage orchestre : l'état (`ActionsPageState`, state.ts), les
// référentiels dérivés (`ActionsPageRefs`), et les blocs d'écran à contexte
// explicite (point 16, 2026-09-08) : bulk-bar, filters-bar, actions-table,
// export, matrix-mode, url-state, meeting-modes, create-action. Elle seule
// écrit l'état et décide de ce qui se redessine.
//
// Bare refs utilisées (résolues via window au runtime) :
//   t, setContent, api, router

import { consumeLastModifiedAnimation } from '../../utils/last-modified-anim.js';
import { defaultActionsFilterState } from '../../domain/actions-filter.js';
import { renderMatrixMode, wireMatrixToggle } from './matrix-mode.js';
import { renderBulkBarInto } from './bulk-bar.js';
import { renderFiltersBarInto } from './filters-bar.js';
import {
  actionsTableHtml,
  createNotesHover,
  openActionEditor,
  wireActionsTable,
} from './actions-table.js';
import { createActionsExport } from './export.js';
import { createActionsUrlSync } from './url-state.js';
import { openExportMenu } from '../../utils/export-menu.js';
import { duplicateActionAsPage, wireNewActionButton } from './create-action.js';
import { consumeMergeAnimation } from './render-page-fragments.js';
import { loadLinkMode, loadViewMode } from './meeting-modes.js';
import {
  createActionsPageRefs,
  createActionsPageState,
  persistFiltersCollapsed,
  persistSort,
} from './state.js';
import type {
  ActionsAction,
  ActionsCommittee,
  ActionsPhase,
  ActionsProgram,
  ActionsProject,
  ActionsStructure,
} from './types.js';

export function renderActionsPage(
  allActionsRaw: unknown[],
  usersRaw: unknown[],
  statusesRaw: unknown[],
  programs: ActionsProgram[] = [],
  projects: ActionsProject[] = [],
  committees: ActionsCommittee[] = [],
  structures: ActionsStructure[] = [],
  phases: ActionsPhase[] = [],
): void {
  const allActions = allActionsRaw as ActionsAction[];
  const refs = createActionsPageRefs({
    users: usersRaw,
    statuses: statusesRaw,
    programs,
    projects,
    committees,
    structures,
    phases,
  });
  const st = createActionsPageState();
  const { selectedIds } = st;
  const findAction = (id: number): ActionsAction | undefined => allActions.find((a) => a.id === id);
  const filteredSorted = (): ActionsAction[] =>
    refs.sortActions(st, refs.applyFilters(st, allActions));
  // Aperçu des notes au survol : une instance par page (cache par action).
  const notesHover = createNotesHover();

  function clearSelection(): void {
    selectedIds.clear();
    renderBulkBar();
    // Décocher visuellement les cases en place sans redessiner la table.
    document.querySelectorAll<HTMLInputElement>('.action-select-checkbox').forEach((cb) => {
      cb.checked = false;
    });
  }

  // Persistance URL : url-state.ts lit et écrit `st` en place. Le retour
  // back/forward passe par le routeur, qui re-dispatche la page entière.
  const urlSync = createActionsUrlSync(st);

  // Volet de filtres (filters-bar.ts) : rend l'état et remonte des patches.
  function renderFiltersBar(): void {
    const host = document.getElementById('act-filters-bar');
    if (!host) return;
    const applyAndRefresh = (patch: Partial<typeof st.filters>): void => {
      st.filters = { ...st.filters, ...patch };
      urlSync.toUrl();
      renderFiltersBar();
      renderTableOnly();
    };
    renderFiltersBarInto(host, {
      state: { ...st.filters },
      collapsed: st.filtersCollapsed,
      today: todayStr(),
      ref: refs,
      handlers: {
        onChange: applyAndRefresh,
        onDateBoundChange: (patch) => {
          st.filters = { ...st.filters, ...patch };
          urlSync.toUrl();
          renderTableOnly();
        },
        onShowDoneChange: (hideDone) => {
          st.filters.fHideDone = hideDone;
          // Afficher les terminées change le JEU DE DONNÉES (non chargées par
          // défaut) : on persiste `done` dans l'URL puis on re-dispatch pour
          // re-fetcher. Les autres filtres, sérialisés dans l'URL, survivent.
          urlSync.toUrl();
          router.dispatch();
        },
        onReset: () => {
          st.filters = defaultActionsFilterState();
          applyAndRefresh({});
        },
        onCollapseChange: (collapsed) => {
          st.filtersCollapsed = collapsed;
          persistFiltersCollapsed(collapsed);
        },
      },
    });
  }

  // Compteur d'actions affichées, partagé liste / matrice.
  function setCount(n: number): void {
    const countEl = document.getElementById('act-count');
    if (!countEl) return;
    countEl.textContent =
      n === 0
        ? t('pageActions.count.empty')
        : n === 1
          ? t('pageActions.count.one')
          : t('pageActions.count.many', { n });
  }

  // Marque l'action comme vue côté serveur et retire tout de suite les badges
  // « Nouveau » / « Modifié ». Un échec réseau est toléré (badge au reload).
  function markSeenLocal(action: ActionsAction | undefined): void {
    if (!action) return;
    if (action.is_newly_assigned || action.is_changed_since_seen) {
      action.is_newly_assigned = false;
      action.is_changed_since_seen = false;
      renderTableOnly();
    }
    if (action.id != null) {
      api.markActionSeen(action.id).catch(() => {
        /* silencieux */
      });
    }
  }

  // Liste ou matrice dans #act-main, selon le mode. Mêmes actions filtrées et
  // triées dans les deux cas ; en matrice, les curseurs ne font que reclasser.
  function renderTableOnly(): void {
    const el = document.getElementById('act-main');
    if (!el) return;
    const filtered = filteredSorted();
    setCount(filtered.length);
    if (st.viewMode === 'matrix') {
      renderMatrixMode(el, {
        actions: filtered,
        thresholds: st.thresholds,
        statusMap: refs.statusMap,
        userMap: refs.userMap,
        onThresholdsChange: (th) => {
          st.thresholds = { ...th };
          urlSync.toUrl();
        },
        onOpen: (id) => {
          markSeenLocal(findAction(id));
          openActionEditor(id, selectedIds);
        },
      });
      return;
    }
    el.innerHTML = actionsTableHtml(filtered, st.sort, {
      statuses: refs.statuses,
      userMap: refs.userMap,
      projectMap: refs.projectMap,
      userId: refs.userId,
      userTeamIds: refs.userTeamIds,
      selectedIds,
    });
    wireActionsTable(el, st.sort, {
      selectedIds,
      findAction,
      notesHover,
      onSelectionChange: renderBulkBar,
      onSort: (next) => {
        st.sort = next;
        persistSort(next);
        renderTableOnly();
      },
      onOpen: markSeenLocal,
      onResetFilters: () => {
        st.filters = defaultActionsFilterState();
        urlSync.toUrl();
        renderFiltersBar();
        renderTableOnly();
      },
    });
  }

  // Barre d'actions de masse (bulk-bar.ts) : sélection, fusion, affectation,
  // rattachement à une réunion. Masquée en matrice.
  function renderBulkBar(): void {
    const el = document.getElementById('act-bulk-bar');
    if (!el) return;
    renderBulkBarInto(el, {
      hidden: st.viewMode === 'matrix',
      selectedIds,
      committees,
      link: st.link,
      onClear: clearSelection,
      onDuplicate: (id) => {
        const src = findAction(id);
        if (src) duplicateActionAsPage(src, selectedIds);
      },
      onAssigned: (ids, cid) => {
        // Union locale des committee_ids pour refléter sans recharger.
        ids.forEach((aid) => {
          const a = findAction(aid);
          if (a) a.committee_ids = [...new Set([...(a.committee_ids || []), cid])];
        });
        clearSelection();
        renderTableOnly();
      },
    });
  }

  // Export XLSX / JSON (export.ts) : filtrées, sélection et filtres lus au clic.
  const actionsExport = createActionsExport({
    selectedIds,
    getFiltered: () => refs.applyFilters(st, allActions),
    getFilters: () => st.filters,
    committeeMap: refs.committeeMap,
  });

  function render(): void {
    // Hydrate l'état depuis le hash AVANT le premier rendu : une URL partagée
    // arrive avec ses filtres.
    urlSync.fromUrl();
    setContent(`<div style="display:flex;flex-direction:column;gap:0">
      <div id="act-count" style="font-size:12px;color:var(--text2);padding:4px 12px 6px 0;font-weight:500"></div>
      <div id="act-mobile-capture" style="display:none"></div>
      <div id="act-filters-bar"></div>
      <div id="act-bulk-bar"></div>
      <div id="act-main" style="min-width:0"></div>
    </div>`);
    renderFiltersBar();
    renderBulkBar();
    renderTableOnly();
    // Bascule liste ↔ matrice : la sélection liste est conservée (masquée en
    // matrice, restaurée au retour).
    wireMatrixToggle(
      () => st.viewMode === 'matrix',
      () => {
        st.viewMode = st.viewMode === 'matrix' ? 'list' : 'matrix';
        urlSync.toUrl();
        renderBulkBar();
        renderTableOnly();
      },
    );
    // Modes réunion (meeting-modes.ts) : pré-cochent les actions déjà liées
    // puis la table se redessine (les cases suivent `selectedIds`).
    if (st.link) {
      // Échec : `null` sort du mode plutôt que d'exposer « Mettre à jour »
      // sur une liste liée inconnue (le toast est posé par loadLinkMode).
      // L'URL n'est PAS réécrite ici : l'utilisateur peut avoir quitté la
      // page pendant le chargement, et le périmètre de la réunion n'a rien
      // à faire dans les filtres mémorisés.
      void loadLinkMode(st.link.meetingId, selectedIds).then((loaded) => {
        st.link = loaded;
        renderTableOnly();
        renderBulkBar();
      });
    } else if (st.meetingViewId) {
      void loadViewMode(st.meetingViewId, selectedIds).then((ok) => {
        if (ok) renderTableOnly();
      });
    }
    // Animations post-MAJ, différées pour laisser le DOM se stabiliser :
    // fusion (drapeau 'merge:lastResult') et édition simple ('lastModified').
    setTimeout(() => {
      consumeMergeAnimation();
      consumeLastModifiedAnimation('actions', (id) =>
        document.querySelector<HTMLTableRowElement>(
          `#actions-list-table tr.action-row[data-action-id="${id}"]`,
        ),
      );
    }, 50);
    document.getElementById('btn-export-actions')?.addEventListener('click', (e: Event) => {
      openExportMenu(
        e.currentTarget as HTMLElement,
        (fmt) => void actionsExport.onExportClick(fmt === 'json' ? 'json' : 'xlsx'),
      );
    });
    // CTA « Ajouter » (create-action.ts) : la page insère l'action créée en tête.
    wireNewActionButton<ActionsAction>(
      { users: refs.users, statuses: refs.statuses, projects, committees, structures },
      (created) => {
        allActions.unshift(created);
        renderTableOnly();
      },
    );
  }

  render();
}
